/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { sendChatMessageThunk, setChatReplyToThunk } from "../../../../redux-magic/store";

/* Component Imports */

import { Button, CircularProgress, Typography } from "@mui/material";

/* Library Functions */

import { uploadChatFiles } from "../../../../lib/chatUpload";

/* Styled Components */

const StackContainer = styled("div")({
	display: "flex",
	flexWrap: "wrap",
	gap: "2px",
	maxWidth: "280px", // Instagram-like width
	borderRadius: "16px",
	overflow: "hidden",
});

const ProgressContainer = styled("div")({
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	zIndex: 2,
});

const ImageWrapper = styled("div")({
	position: "relative",
	width: "100%",
	height: "100%",
});
const GridImage = styled("img")({
	objectFit: "cover",
	borderRadius: "0",
	"&.single": {
		width: "180px",
		height: "180px",
	},
	"&.double": {
		width: "139px",
		height: "180px",
	},
	"&.triple": {
		"&.large": {
			width: "280px",
			height: "186px",
		},
		"&.small": {
			width: "139px",
			height: "92px",
		},
	},
	"&.quad": {
		width: "139px",
		height: "139px",
	},
});

const MessageText = styled("div")({
	padding: "8px 12px",
	fontSize: "14px",
	color: "#333",
	width: "100%",
	wordBreak: "break-word",
	maxWidth: "280px",
});
interface UploadProgress {
	fileId: ObjectId | null;
	percentage: number;
	stage: string;
}

interface FileAndMore {
	file: File;
	mime_type: string;
}
const ImageStackUploadPreview = ({
	images,
	session,
	dispatch,
	router,
	profile_context,
	realtime_websocket,
	receiver_user_id,
	initial_load_done,
	messages_loading,
	chat_id,
	pending_uploads,
	current_message,
	setCurrentMessage,
	sendFiles,
	setSendFiles,
	setSelectedFilePreview,
	setPendingUploads,
	reply_to_message_id,
}: {
	images: string[];
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	realtime_websocket: any;
	receiver_user_id: string | ObjectId;
	initial_load_done: boolean;
	messages_loading: boolean;
	chat_id: string;
	pending_uploads: Array<FileAndMore>;
	current_message: string;
	sendFiles: boolean;
	setCurrentMessage: React.Dispatch<React.SetStateAction<string>>;
	setSendFiles: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedFilePreview: React.Dispatch<React.SetStateAction<Array<string>>>;
	setPendingUploads: React.Dispatch<React.SetStateAction<Array<FileAndMore>>>;
	/** The message the viewer is replying to, "" when none (18 Oct 2026: photos used to drop it). */
	reply_to_message_id?: string;
}) => {
	/*

	& One photo send (chat audit F3, 17 Oct 2026): upload every picked photo, then send one message
	& with them (the server tells the other side). Three things were wrong before:
	&   - every photo was sent with `mime_type: ""` (it read a list that was never filled), so the
	&     server could not tell a photo from anything else; it now sends each file's real type;
	&   - a failed upload or send left the spinner up forever; it now says so, with Try again and
	&     Discard;
	&   - a personal sender crashed on `new ObjectId("")` for the business id; a business id goes up
	&     only while acting as a business, as in the app.
	& A retry re-uploads only when the upload itself failed, and resends with the same message id,
	& so a send that did land is answered with the stored message instead of a second copy.

	*/

	type Phase = "uploading" | "sending" | "failed";

	const [phase, setPhase] = React.useState<Phase>("uploading");

	// One batch per mount: the effect below can re-run on a re-render while a send is in flight.
	const startedRef = React.useRef<boolean>(false);

	// The uploaded files, kept for a retry that only needs to resend.
	const uploadedRef = React.useRef<Array<{ file_id: string; mime_type: string }> | null>(null);

	// The message id, kept across retries (the server stores an id only once).
	const messageIdRef = React.useRef<string>(new ObjectId().toString());

	// The quote, taken once when the batch starts, so a retry quotes the same message.
	const replyToRef = React.useRef<string>(reply_to_message_id || "");

	const viewerUserId: string = session?.user?.dbUser?._id ? String(session.user.dbUser._id) : "";

	const actingAsBusiness: boolean =
		profile_context?.is_business_profile === true &&
		ObjectId.isValid(String(profile_context?.business_profile_id || ""));

	// The shared chat upload (lib/chatUpload.ts): real mime types, the person's own file, and a
	// business id only while acting as a business.
	const uploadAll = (validFiles: Array<FileAndMore>): Promise<Array<{ file_id: string; mime_type: string }> | null> =>
		uploadChatFiles({ session, profile_context, chatId: chat_id, files: validFiles, useCase: "chat_image" });

	const clearComposer = () => {
		setCurrentMessage("");
		setSendFiles(false);
		setSelectedFilePreview([]);
		setPendingUploads([]);
	};

	const sendBatch = async (validFiles: Array<FileAndMore>) => {
		if (!uploadedRef.current) {
			setPhase("uploading");
			uploadedRef.current = await uploadAll(validFiles);
			if (!uploadedRef.current) {
				setPhase("failed");
				return;
			}
		}
		setPhase("sending");
		const message_id: string = messageIdRef.current;
		const outcome = (await dispatch(
			sendChatMessageThunk({
				chat_id: chat_id,
				message_id: message_id,
				sender_is_business_profile: actingAsBusiness,
				sender_user_id: viewerUserId,
				sender_business_profile_id: actingAsBusiness ? String(profile_context.business_profile_id) : "",
				message_body: current_message,
				attachments: uploadedRef.current,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				...(replyToRef.current ? { reply_to: replyToRef.current } : {}),
			}),
		)) as { ok: boolean } | undefined;
		if (!outcome || !outcome.ok) {
			setPhase("failed");
			return;
		}
		// The server tells the other side (chat/send-message relays by user id).
		if (replyToRef.current) dispatch(setChatReplyToThunk(null));
		clearComposer();
	};

	const validFilesOf = (): Array<FileAndMore> =>
		(pending_uploads || [])
			.filter((fileAndMore) => fileAndMore.file && fileAndMore.mime_type)
			.map((fileAndMore) => ({
				file: fileAndMore.file,
				mime_type: fileAndMore.mime_type,
			}));

	React.useEffect(() => {
		if (startedRef.current) return;
		if (pending_uploads && pending_uploads.length > 0 && images && images.length > 0 && sendFiles) {
			const validFiles = validFilesOf();
			if (validFiles.length > 0) {
				startedRef.current = true;
				sendBatch(validFiles);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pending_uploads, images, sendFiles]);

	const isLoading: boolean = phase === "uploading" || phase === "sending";

	const renderImageGrid = () => {
		switch (images.length) {
			case 1:
				return (
					<StackContainer>
						<GridImage
							src={images[0]}
							alt="preview-0"
							className="single"
						/>
					</StackContainer>
				);

			case 2:
				return (
					<StackContainer>
						{images.map((src, index) => (
							<GridImage
								key={index}
								src={src}
								alt={`preview-${index}`}
								className="double"
							/>
						))}
					</StackContainer>
				);

			case 3:
				return (
					<StackContainer>
						<GridImage
							src={images[0]}
							alt="preview-0"
							className="triple large"
						/>
						<div style={{ display: "flex", gap: "2px" }}>
							{images.slice(1).map((src, index) => (
								<GridImage
									key={index + 1}
									src={src}
									alt={`preview-${index + 1}`}
									className="triple small"
								/>
							))}
						</div>
					</StackContainer>
				);

			default:
				return (
					<StackContainer>
						<div style={{ display: "flex", gap: "2px" }}>
							<GridImage
								src={images[0]}
								alt="preview-0"
								className="quad"
							/>
							<GridImage
								src={images[1]}
								alt="preview-1"
								className="quad"
							/>
						</div>
						<div style={{ display: "flex", gap: "2px" }}>
							<GridImage
								src={images[2]}
								alt="preview-2"
								className="quad"
							/>
							<ImageWrapper
								sx={{
									position: "relative",
									width: "139px",
									height: "139px",
									overflow: "hidden",
								}}
							>
								<GridImage
									src={images[3]}
									alt="preview-3"
									className="quad"
								/>
								{images.length > 4 && (
									<div
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											background: "rgba(0,0,0,0.5)",
											color: "white",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: "16px",
											fontWeight: "bold",
										}}
									>
										+{images.length - 4}
									</div>
								)}
							</ImageWrapper>
						</div>
					</StackContainer>
				);
		}
	};

	return (
		<div style={{ position: "relative" }}>
			{isLoading && (
				<ProgressContainer
					sx={{
						position: "absolute",
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						zIndex: 3,
						borderRadius: "16px",
					}}
				>
					<CircularProgress sx={{ color: "#FFF" }} />
				</ProgressContainer>
			)}
			{phase === "failed" && (
				<ProgressContainer
					sx={{
						position: "absolute",
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(0, 0, 0, 0.6)",
						zIndex: 3,
						borderRadius: "16px",
						flexDirection: "column",
						gap: "0.5rem",
						padding: "0.75rem",
						boxSizing: "border-box",
					}}
				>
					<Typography
						variant="body2"
						sx={{ color: "#FFF", textAlign: "center", fontWeight: 500 }}
					>
						{router.locale === "ar-AE"
							? "تعذر إرسال الصور"
							: router.locale === "kn-IN"
								? "ಫೋಟೋಗಳನ್ನು ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ"
								: "Couldn't send these photos"}
					</Typography>
					<div style={{ display: "flex", gap: "0.5rem" }}>
						<Button
							size="small"
							variant="contained"
							disableElevation
							onClick={() => sendBatch(validFilesOf())}
							sx={{ textTransform: "none", borderRadius: "16px" }}
						>
							{router.locale === "ar-AE" ? "حاول مرة أخرى" : router.locale === "kn-IN" ? "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ" : "Try again"}
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={clearComposer}
							sx={{ textTransform: "none", borderRadius: "16px", color: "#FFF", borderColor: "#FFF" }}
						>
							{router.locale === "ar-AE" ? "إلغاء" : router.locale === "kn-IN" ? "ತಿರಸ್ಕರಿಸಿ" : "Discard"}
						</Button>
					</div>
				</ProgressContainer>
			)}
			{renderImageGrid()}
			{current_message && <MessageText>{current_message}</MessageText>}
		</div>
	);
};

export default ImageStackUploadPreview;
